import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from './ui/use-toast';
import { ArrowLeft, Upload, FileText, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input'; // Import Input for the title field
import { useProjectStore } from '@/stores/projectStore';

interface ScriptUploadProps {
  onAnalysisTriggered: (analysisData: any) => void;
  onBack: () => void;
  onUploadSuccess: (scriptId: string) => void;
}

interface UploadedFileStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  scriptId?: string;
  error?: string;
}

const SAMPLE_SCRIPT = `INT. RALPH PUCCI GALLERY [VISIBLE LOGO - GALLERY]- AFTERNOON
TIGHT on a painting of a young boy in ornate garb. His
expression is blank, stoic. Certainly not happy.
ZOOM OUT to reveal DREW (37, Asian, the reliable type),
donning a name tag that reads: PARTNER GILE CAPITAL. He's
lost in thought, staring at the painting in the midst of an
upscale art gallery. GALLERY GUESTS (bankers, artists,
business people) mingle in chic attire [PRODUCT USE - DESIGNER MEN’S APPAREL] and name tags.
Suddenly, a WAITER (mid 20's, Latino) approaches him, holding
a tray of drinks.
WAITER
Do you know what you want?
A beat. Drew then surfaces from his daydream, turning to the
waiter. A slight smile of confusion.
WAITER (CONT’D)
What would you like to drink?
The waiter presents Drew with a range of choices: A fruity
cocktail, a glass of champagne, a bourbon on the rocks and a
Perrier [PRODUCT USE - SOFT DRINK].
DREW
Honestly, I just want to get
through this.
The waiter looks expressionless. Drew awkwardly smiles.
DREW (CONT’D)
I’m good for now. Thank you.
The waiter nods his head and begins to walk away with his
tray. As he walks, DEAN (early 40s, white, acerbic, brilliant
and painfully to-the-point; his name tag: SENIOR PARTNER)
comes up to him and grabs the bourbon [PRODUCT USE - ALCOHOLIC BEVERAGE] off of the tray. He
takes a large swig, downing the drink quickly, dropping the
drink back on the tray almost as fast as he took it.
DEAN
What are we too cheap to actually
pour a full drink? Get me another
one of these, will you?
The waiter scurries off to get another drink while Dean sees
Drew in the distance as he wipes his face. He approaches him.
DEAN (CONT’D)
(very earnestly.)
Excuse me, have we met? You look a
lot like my colleague Drew but he
hasn't shown up to work in six
months, so it can't be him.
I’m kidding. Nice to see you.
Congratulations!
DREW
For what?
DEAN
Didn’t you have a kid or something?
A sore spot for Drew, he pauses to respond, just as he’s
about to answer...
DEAN (CONT’D)
Oh, and how’s your mom. She doing
better?
DREW
I didn’t have a kid. And my mom is
dead. I was off for bereavement.
DEAN
Oh.
A beat. Neither knows what to say and look at each other
awkwardly. The waiter comes back over and hands Dean another
bourbon - this time, full to the brim. No ice.
DEAN (CONT’D)
Cancer is a terrible thing. I do
one of those charity races for the
cure every year. Clients love it.
There's no better way to connect
than over grief.
He takes a swig of his drink. Drew brushes past it.
DREW
I've always admired your empathy
Dean. Anything I should know before
I...
Drew surmises the crowd with trepidation.
DREW (CONT’D)
Get in there.
His enthusiasm's unconvincing—even to himself.
2.
DEAN
You know, the world's on fire, so
business is good.
DREW
Oh, speaking of which...I sent in
the extension request for the IC
tomorrow, did you get it?
Dean begins to respond, but spots someone more important in
the distance and motions his exit. He gives Drew a sly wink
as he exits.
DEAN
Welcome back.
Another hard pat on the shoulder and he's off. Drew ruminates
for a minute. He looks around the room, takes a deep breath
and walks into the crowd.
INT. UPPER WEST SIDE CO-OP - SAME TIME
INSERT: Penthouse
AP (36, white, mixing a Bode [PRODUCT USE - DESIGNER MENS APPAREL] outfit with a Trinity hat [PRODUCT USE - DESIGNER MEN’S APPAREL]) is
asleep on his couch in the middle of the day. Weed, halfeaten
food, and miscellaneous items lay strewn about. His
phone rings [PRODUCT USE - SMARTPHONE] — it's his dad. A picture of him as a young boy
with his father fills the screen.
RING. RING. RING.
After a beat, AP wakes up, looks at the time - he overslept.
He walks to the fridge [PRODUCT USE - REFRIGERATOR], hungover and disheveled. On the
fridge he sees a note he wrote to himself.
INSERT STICKY NOTE: FINISH COMMISSION
He pulls it off and grabs a snack [PRODUCT USE -  SNACK] and bottle of electrolytes [PRODUCT USE - SOFT DRINK].
He goes into his room to play video games [PRODUCT USE - VIDEO GAMES]. Another sticky
note:
INSERT STICKY NOTE: DO DAD'S COMMISSION
He pulls it off. He walks into his closet to grab workout
gear  [PRODUCT USE - MEN’S WORKOUT APPAREL]. Another sticky note:
INSERT STICKY NOTE: DO IT NOW OR NO MORE MONEY
3.
INT. UPPER WEST SIDE CO-OP - LATER
AP walks into his apartment, drenched in sweat and listening
to music [PRODUCT USE - MUSIC STREAMING]. He alternates sipping his Electrolyte with a straw
and humming the lyrics to “I only want to be with you” by
Dusty Springfield. He walks into the kitchen and tosses the
bottle into the trash at first. He then takes it out to put
the bottle and the straw into the recycling bin.
AP
I just want to be beside you
everywhere...As long as we're
together, honey, I don't care...
Cause you've started something, oh,
can't you see?...
That ever since we met you've had a
hold on me...
His phone beeps - this time a text. It’s his dad again.
INSERT PHONE SCREEN: Pick up your phone
He texts him back.
INSERT PHONE SCREEN: Hard at work painting. Can’t disrupt
process!
AP (CONT’D)
No matter what you do...I only want
to be with you.
INT. UPPER WEST SIDE CO-OP - LATER
Fully dressed after a shower, AP picks up one of his many
finished canvases laying on the floor of his bedroom and
scurries out of his apartment.
INT. RALPH PUCCI GALLERY - SAME TIME
Gallery Guests gather around JOAN (late 50's, Black, firm,
commands a room)—her name tag: MANAGING PARTNER. She's
flanked by Dean. He eyes Joan, miffed. A handful of men with
similar name tags with SENIOR PARTNER stand beside them while
others file in the back.
JOAN
Can I have everyone's attention,
please? Don't worry, the canapés
aren't going anywhere. They're
quite good, aren't they?
4.
A perfunctory laugh from the audience. Standing far back from
Joan is MARTY (37, Black, handsome, a charmer). He wears an
impeccably tailored suit [PRODUCT USE - MEN’S DESIGNER APPAREL], but is missing a name tag. He
stands alongside peers, chatting with what seems to be a
permanent smile.
JOAN (CONT’D)
Welcome to Gile Capital's bi-annual
ascension ceremony. We’ve elected
an incredible group of
professionals who truly embody
Gile’s values and I cant wait to
celebrate this cycle’s class
alongside their co-workers and
clients. A big thank you to our
friends at Ralph Pucci for hosting
us...
Marty looks on, sipping his champagne. He’s interrupted by a
phone call. Its his wife HANNAH (32, if Parker Posey played a
mix of Charlotte York and Elle Woods). He finds a quiet
corner to take the call.
INTERCUT INT. GRAMERCY TERRACE - AFTERNOON
Hannah sits by a table on a terrace overlooking Manhattan.
Various swatches [VISIBLE LOGO - DESIGNER FURNITURE] and design books lay out across the table.
HANNAH
Well if it isn’t Giles newest,
sexiest and savviest Entertainment
Partner!
MARTY
Hey baby. How’s it going?
HANNAH
I am so upset that I’m not there
for your ceremony. I so wanted to
be there to celebrate you.
MARTY
They just wanted to pare it back
this year. You know, keep it
simple. And besides we have our own
celebration this weekend.
HANNAH
You know Anna and Michael got their
promos too? We really should
celebrate them at the party.
(MORE)
5.
I mean this was Michael’s 3rd time
going up and I swear his wife was
going to leave him if he didn’t get
it.
As Joan talks, a group of partners begin bringing chairs
towards her.
MARTY
(A beat, internalizing). Yeah, but
I’d hope she’d support her husband
regardless.
HANNAH
Well she never sees him, he’s
always stressed and if he didn’t
get it this time surely he would’ve
been fired. It’s like, you do all
of that work and have nothing to
show for it.
One by one, individuals are called up to where Joan is giving
her speech and sit down in the chairs - now several lined up
one by one behind her. Marty tries to stay discreet so as no
one will hear him.
HANNAH (CONT’D)
You support the person you think
you married. And if they end up not
being who you think they are, then
maybe you reconsider. Right?
A beat. A faint applause is heard in the background.
MARTY
Right. Hey babe I got to go. I’ll let you know how the rest
of it goes. Love you.
HANNAH
Celebrate this moment, Martin. You
worked so hard for this. WE worked
so hard for this. And its finally
here.
Five individuals sit in chairs behind Joan, beaming. Each
wears a sash with a faux crown and a gold plated “PARTNER”
name tag, freshly placed on their jackets. One chair remains
empty.
HANNAH (CONT’D)
6.
JOAN
And the newest partner of the Media
& Entertainment practice…Jessica
Tully! Congratulations, Jessica!
A woman, JESSICA (late 30s, white) walks past Marty and goes
up to sit down in the lone empty chair while a sash is put
around her neck and a crown is put on her head. Marty claps
and looks on, dejected.
INT. RALPH PUCCI GALLERY - ACROSS THE ROOM - A LITTLE LATER
AP stealthily enters the gallery scanning the room: the coast
is clear. He quickly begins to stage his art on an easel next
to the others.
MARTY
(disguising his voice)
Excuse me young man, what do you
think you’re doing?
AP
(turns, startled)
Oh! Uh - I’m one of the curator..
He rolls his eyes, recognizing his friend.
AP (CONT’D)
Fuck off.
MARTY
(pointing to the painting)
You're shameless.
AP
Entrepreneurial.
Drew walks up, smiling, but not quite present. He looks at AP
confused.
DREW
The hell are you doing here?
MARTY
It’s called trespassing.
AP
My dad is friends with Ralph.
MARTY
Of course he is.
7.
AP
What’s that supposed to mean?
MARTY
Exactly what you think it means.
DREW
Guys, not now. This is painful
enough as it is.
AP finishes getting the painting on, with a look of relief
and satisfaction. He hands Drew his phone.
AP
Success! Can you take a video? I
started a store on TikTok [PRODUCT MENTION - SOCIAL MEDIA]...Oh,
wait I almost forgot.
AP reaches into his pocket and pulls out a red sticker. He
puts it on the painting.
MARTY
Unhinged.
Drew, amused, starts to take a video when the phone rings.
It’s APs dad again. Drew hands AP the phone.
DREW
Uh, your dad is calling.
AP
Just let it go to voicemail.
Drew declines the call.
INSERT PHONE SCREEN: MISSED CALL - DAD (6x)
DREW
He’s called you six times?
AP
I owe him a painting that I haven’t
quite finished yet.
DREW
Are you sure thats what its about?
MARTY
You really think he cares that much
about you slacking on a painting he
probably doesn’t want?
Drew hands AP back his phone. Marty tries to lighten the
mood.
8.
DREW
I think I’ve made it as long as I
could. I’m cooking tonight, anyway.
MARTY
A bit early. Even for you.
He nudges Drew who gives a half-hearted smile.
DREW
See you this weekend?
MARTY
Party of the summer!
AP
I'll be there.
With that, Drew exits. Marty and AP linger, watching him go.
EXT. DREW'S NEW YORK APARTMENT - BALCONY - EVENING
Drew, still in his work clothes, stares out into the city
from the balcony of his upscale, open format apartment. He's
so deep in thought, when his husband, BEN (42, folksy,
positive and good humored and wearing SCRUBS), opens the
balcony door sticking his head out.
BEN
Home!
He closes the door. Drew silently collapses.
INT. DREW'S NEW YORK APARTMENT - CONTINUED
Drew preps ingredients [PRODUCT USE - KITCHEN SUPPLIES]. He's organized and hyper-focused.
Ben, drying his hair from a shower, leans on the kitchen
island, and watches Drew.
BEN
How was the event?
DREW
The usual.
He chops vegetables vigorously—a stress-reliever.
BEN
First week back is okay then?
Drew looks up—finally—and stops chopping. Reaching into the
cabinet he hands Ben plates and silverware.
DREW
It is what it is.
9.
DING. A Grindr notification [PRODUCT USE - SOCIAL MEDIA] from Ben's pocket. Reaching in -
he silences his phone unnecessarily quick. Drew notices and
stops chopping for a beat. He knows the sound, but doesn’t
acknowledge it.
DREW (CONT’D)
Honestly, it was hard to be back
there. On the one hand it feels
like I never left, but at the same
time it also feels...
Drew seems lost for words, his body language reflecting a
general sense of malaise. Ben makes a move to kiss Drew on
the forehead. Drew receives it reluctantly and shoos him to
go set the table. Ben complies, but with a burst of energy.
BEN
You know, Natalie Maines [PRODUCT MENTION - PODCAST] has this
podcast. She talks about life
transitions and being stuck in
cycles. One thing she took up
during the whole Bush fiasco was
line dancing. Maybe we could take
some classes? Before the baby
comes?
(he catches himself)
Whenever we decide to try again.
DREW
I don't think I have the
coordination for all those turns.
BEN
I'll teach you. Just need to dust
off my Wranglers. If I wasn't
working doubles this weekend, I'd
start a line at Marty's. Get the
whole Hamptons jumpin'.
Drew finishes setting the table.
DREW
(laughs)
Sure. I'll think about it.
Drew continues to cook. Ben's face falls. "I'll think about
it" always means "no." Ben reaches for his phone. We don't
see the screen. Ben reads the message, subtly beaming.
Catching himself, he looks to Drew whose back is still
turned. He stares at Drew for a moment, hoping he'll turn
around. He doesn't. He picks back up the phone and bashfully
types a reply while texting.
10.
Drew continues to chop, unnoticing. DING, an email
notification from Drew's work laptop [PRODUCT USE - LAPTOP] behind him. Without
looking, he slams it shut.
INT. GILE CAPITAL - DREW'S OFFICE - THE NEXT MORNING
Drew walks into his upscale private office befitting of a
private equity partner. He logs onto his work computer
navigating to his email. He sees it.
INSERT ON SCREEN: Your Investment Committee extension request
has been denied. The meeting will proceed as planned Thursday
the 18th at 2:30pm.
DREW
Really?
He collapses into his chair and checks the time on his phone:
8:20am. He leans back in his chair. Anger, frustration, angst
bubbles up; he's ready to scream but...resists—SNAP. He
pushes it down. He logs into his company slack channel [PRODUCT USE - COMMUNICATION PLATFORM] and
pings Dean:
INSERT ON SCREEN: I have been back for 3 days, can I
seriously not have more time to prepare for this meeting?
Dean comes online and reads the message. The typing bubble
shows thinking.
INSERT ON SCREEN: 3 days was your prep time. Welcome back!
DREW (CONT’D)
Bastard.
INT. GILE CAPITAL - DREW'S OFFICE - LATER
A stack of freshly printed booklets rest on Drew's desk. They
read: Decatur & Leavenworth Merger - Driving consolidation in
plastics.
INT. GILE CAPITAL BATHROOM - SECONDS LATER
Drew, looks at himself in the mirror. He pumps himself up,
shaking off the nerves.
DREW
You know, pressure. You rise to the
occasion. You've got this.
He checks his watch. Time to face the music. As he exits -
deadpan.
11.
DREW (CONT’D)
I love my job.
INT. GILE CAPITAL - CONFERENCE ROOM - MOMENTS LATER
A stream of SUITS (various ages, mostly male and white) file
into the room, sitting at a long conference table while Drew
stands at the front. Confident. Dean walks in, carrying a RED
RUBBER BALL. Joan kicks off the meeting.
JOAN
Alright everyone, let's get
started.
JOAN (CONT’D)
Today's IC we're here to discuss a
potential acquisition of Decatur on
behalf of Leavenworth, valued at
$3.2B. Drew is the sponsor of the
deal. Drew?
Drew steps up, energized. He claps. No one else does.
DREW
Thanks, Joan.
Too much. He dials it back.
DREW (CONT’D)
So we're here to review a merger of
two leaders in the food service and
packaging industry. This deal would
drive synergies in manufacturing,
distribution and supply pain-
DEAN
Pain?
A small chuckle from the room. Drew rolls with it.
DREW
Chain. Supply Chain. I misspoke.
DEAN
You also misspelled. There's a typo
on your page and I can't read this
chart.
He dramatically flips through the booklet, scanning for more
errors. Drew tries to shake it off.
12.
DREW
As I was saying. This merger would
drive significant synergies in the
supply chain that-
DEAN
Could you be more specific?
Dean’s interruption rattles Drew, Dean begins to squeeze his
red ball.
DREW
Sure. The two currently service 30%
of their category and combining
them will allow us to bring down
unit costs and improve margins.
DEAN
How?
DREW
At 30% they'll have better pricing
power. Their suppliers will-
DEAN
How many suppliers do they have
between the two of them? How
reliable are they?
DREW
Four. Very.
DEAN
Define very.
Drew's eyes narrow. The Suits lean in. Joan listens intently.
DEAN (CONT’D)
Suppliers-wise. Define very.
DREW
I'd like to get past page one.
Dean holds up his hand.
DEAN
I’d like you to answer my question.
You come up here and say the deal's
about synergies. Show me the
fucking synergies.
Drew bites back anger as Dean squeezes the red ball with ever
more intensity, leaning back in his chair.
13.
Drew looks to Joan for some support, but she flips through
the booklet, taking in the information. A good amount of
Suits shake their heads, jotting down notes.
Before Drew can recover...
DEAN (CONT’D)
Four suppliers for 30% market share
seems low. What is the category
average?
DREW
It's low and there's some risk. But-
He turns to his slides, blanking. Joan reads the room,
gauging the sentiment on the deal: no confidence.
JOAN
Drew, Dean has highlighted valid
concerns. Tell me. How would you
mitigate risk?
Once again, before Drew can respond-
DEAN
If I may, Joan. I was just with
Needleman and they have a similar
supplier issue. Frankly, they're
overexposed. Before doing any deal,
why don't we work with our ops team
to help both clients diversify
suppliers?
Joan nods, impressed and in agreement.
DEAN (CONT’D)
I mean, there's no urgency for this
deal. Right, Drew?
Dean throws the ball at Drew attempting to belittle him, who
catches it and throws it back with a bit too much force. Dean
swivels out of the way, letting the ball hit the wall,
creating a commotion. The Suits huff and murmur at the lack
of decorum.
A last attempt from Drew—he raises his voice a bit too much.
DREW
These risks are not deal blocking.
If I could explain the full plan-
14.
JOAN
Drew. The deal is not approved.
This IC is adjourned. We'll give
everyone time back.
The room files out as Drew remains, stunned. Dean tauntingly
picks up the ball from the floor, slamming it on the table,
eyeing Drew as he exits.
Now alone, Drew grabs the ball, motions to throw it, but
stops himself. And, with a deep breath, pockets the ball
exiting. He follows Dean into his office, incredulous.
INT. GILE CAPITAL - DEAN’S OFFICE - MOMENTS LATER
Dean casually sits down in his office while Drew comes in
huffing behind him. Drew struggles to hold back his anger.
DREW
What the...what was that?
DEAN
Whatever do you mean?
DREW
Dean, I told you I wasn’t ready for
that review. I don’t understand
why...
DEAN
You weren’t ready? You’re a partner
- you always need to be ready.
DREW
I just got back from..
DEAN
Bereavement, I know. I’m sorry
about your mother. But guess what -
you still have a job to do, clients
to serve and deals to close. The
world doesn’t stop just so you can
wipe your tears.
EXT. UPPER WEST SIDE STREETS - DAY
AP walks various blocks in the Upper West Side, carefree. He
dances to music playing in his earphones [PRODUCT USE - HEADPHONES], humming along to
his favorite tunes. He walks up to his co-op, cheerful and
carrying a newly bought messenger bag. The DOORMAN (50s,
loyal) gives him a foreboding look.
15.
INT. UPPER WEST SIDE CO-OP [VISIBLE LOGO - REAL ESTATE]- MOMENTS LATER
AP walks into his lobby and into the elevator. He continues
to whistle while he pulls out his keys and mutters to
himself.
AP
I gotta figure out what I’m going
to wear this weekend.
INT. UPPER WEST SIDE CO-OP - PENTHOUSE FLOOR - MOMENTS LATER
AP walks down the hall, his head buried in his phone, as he
approaches the door he notices an eviction notice on the
door.
AP
What the hell...
INSERT NYC EVICTION NOTICE
AP puts in his keys to open the door. The knob doesn’t turn;
the lock has been changed. He bangs at the door hopelessly.
AP (CONT’D)
What?! Fuckity fuck fuck!
INT. INVESTMENT FIRM OFFICE - LATER
AP’s father, ADAM (Early 60s, white, H.W. Bush inspired) sits
at his desk. His office is large, filled with art, plants and
a seating area right out of the Oval Office. He studiously
reads a large file, taking copious notes when AP barges in
like a bull. He is out of breath, still carrying his
messenger bag.
AP
What the hell did you do?
ADAM’s ASSISTANT (20s Asian woman, terrified) comes up behind
AP, shrugging hopelessly. Adam waves her away, fully
expecting his son’s outburst.
ADAM
There he is.
AP
You kicked me out my house?
Triggered, Adam stands up and slams his table.
16.
ADAM
Correction, I kicked you out of MY
house. That I pay for, with MY job.
And if you could’ve been bothered
to answer your goddamn phone the
last thousand times I’ve been
trying to call, you’d know.
AP
But, but...I’ll finish the painting
I promise. I just need a few more
days. And I’ve got some good leads
on selling other work. Ralph even
featured me in his latest show!
ADAM
Boy, you and I both know thats a
lie. Not only did you completely
fail to take the one thing I ask
you to do seriously, you have the
audacity to barge into a family
friend’s event and masquerade as if
you actually belonged there. How
dense do you think we are?
AP
I...
ADAM
You are 36 god damn years old with
the discipline of a toddler. Well
guess what - no more. No apartment,
no allowance, no more free rides.
Adam goes back to sit in his chair, growing weary of the back
and forth with his obstinate child.
AP
What?! But what am I supposed to
do?
ADAM
The same thing every other person
on earth does! Get a damn job and
learn how to take care of yourself.
A beat.
AP
Papa.
For a moment, Adam realizes the weight of his words and
begins to question whether he went too far.
17.
ADAM
What?
AP
Can’t that wait until after I get
back from the Hamptons?
INT. PENN STATION TRAIN HALL - DAY - LATER
Drew stands at the track, looking around for his friends.
Heavy from the morning, he looks like he would rather be
anywhere else. There are scores of people surrounding him,
all rushing to get on the double decker train. Travel bag and
carryon [PRODUCT USE - LUGGAGE] in tow, he checks his watch for the time.
INSERT APPLE WATCH TIME [PRODUCT USE - SMARTWATCH]: 4:02
CONDUCTOR (O.S.)
Final Call for the 4:06 Cannonball
train to Montauk. Track 19.
AP spots Drew and waves. He carries his empty messenger bag
and nothing else. Distracted and emotionally drained from
earlier, he lackadaisically comes up to Drew on the platform.
They are both equally emotionally vacant and too dissociative
to notice the others behavior.
DREW
Hey.
AP
Hey.
DREW
Where’s all your stuff?
AP starts to tell Drew what actually happened, but demurs and
shifts course.
AP
Where’s Marty?
DREW
He’s not here yet and they’re going
to leave in 2 minutes.
AP
Let's just get on. If he misses it,
theres always Blade.
18.
INT. LIRR TRAIN - CONTINUOUS
Drew and AP board the train, which is completely full. A mix
of Gen-Z heading to their family weekend house, GAYS heading
to Fire Island and EVERY DAY PEOPLE just trying to get home
to Long Island. There isn’t a single seat in sight - AP and
Drew elbow their way through trying to find a place to squat.
The two walk into a new car and find Marty, who has a four
top to himself as he chats up a few 20-something INFLUENCERS
desperate for a party invite.
MARTY
You can totally live stream it and
I’d be happy to introduce you to
some brands. Oh there you guys are -
saved you some seats.
AP and Drew sit down in the two open seats, Marty’s Tumi [VISIBLE LOGO - LUGGAGE]
taking up a seat for itself.
INT. LIRR TRAIN - LATER
Drew and Marty sit in silence, scrolling their phones. AP
sleeps, snoring.
CONDUCTOR (O.S.)
The next stop is, Westhampton.
DREW
Hey, do you have an Advil [PRODUCT MENTION - MEDICINE] or
something?
MARTY
Yeah, sure.
Marty pulls one of a dozen pill bottles from his bag—that's
almost stuffed to the brim with paraphernalia—and shakes it.
MARTY (CONT’D)
Lemme just make sure these are the
right pills.
Drew scoffs. Marty takes out an Advil - its marked as such -
puts it in Drew’s mouth and lightly taps his cheek.
MARTY (CONT’D)
Let the grownups have their fun.
19.
INT. LIRR TRAIN - LATER
AP
(to Marty)
Can I borrow some cash? My card's
scratched to hell.
MARTY
Why don't you ask Drew.
AP
Because I love him.
Marty rolls his eyes and hands AP a twenty.
AP (CONT’D)
That’s it?
Marty's phone rings. It's his wife.
MARTY
Hey, baby.
INT. MARTY'S HAMPTON HOME - BEDROOM/LIRR TRAIN - INTERCUT
HANNAH arranges several high-end lingerie and sleepwear
garments [PRODUCT USE - FEMALE LINGERIE] on a king- sized bed in a massive South Hampton
home.
HANNAH
I need you to pick up a few things.
Marty pulls the phone from his ear and shoots Drew a "here we
go" look. Drew shakes his head and leans back for some
shuteye. Without thinking, he pulls out the rubber ball and
squeezes it. Marty and AP notice and shoot each other "wtf"
glances, smirking.
Phone, still away from Marty's ear, Hannah continues to
rattle off a laundry list of items.
MARTY
Why don't you just shoot me a text?
HANNAH
I need you to go by the dry
cleaners and grab my outfit for the
Parrish benefit. Oh and can you
stop by Citarella [PRODUCT MENTION - GROCERY STORE]- the caterers
didn’t include a Kosher option and
I don’t want the Rosenthals talking
behind my back.
(MORE)
20.
And Martin, PLEASE don't get the
cheap lox...get the good lox.
Marty shrinks in his seat, weighed down by his wife.
HANNAH (CONT’D)
And remember to sign the card for
Anna and Michael when you get here
we’re going to take them out to
dinner.
MARTY
You got it, babe.
He hangs up. AP mimics Hannah's mannerisms.
AP
Get the good lox, Martin.
Marty flips him off.
AP (CONT’D)
Oh and uh, Drew can I stay at your
place this weekend?
Drew and Marty look at AP confused. AP senses their reaction
and deflects.
AP (CONT’D)
My parents are getting new bidets
put in. Its much cleaner you know.
MARTY
Gross.
EXT. NEW YORK CITY - DAY
An aerial shot of the Long Island Rail Road leaving Manhattan
and into East Hampton. Various shots of the hamlet showcase
the picturesque beauty of a summer day in the beach town.
INT. DREW AND BEN'S EAST HAMPTON HOME - LATER
Drew walks in and collapses on the couch in his living room.
Exasperated from the train ride, the day, the week. Drew lays
his head on a quilt [PRODUCT USE - BEDDING], an elaborate bojagi design that has long
been in his family. AP walks out to the backyard out of view,
checking out the latest landscaping.
AP (O.S.)
Did you guys just put these
hydrangeas in? They’re so nice.
HANNAH (CONT’D)
21.
Drew rubs his head against the quilt, closing his eyes for a
beat. He opens them squarely focused on a clear container
sitting on a coffee table [PRODUCT USE - FURNITURE], filled with Jumok - cremation
beads of his mother. A long gaze at the Jumok dials him back
into reality; he plugs in his security key to his laptop,
navicates to his inbox and opens the message.
AP (O.S.) (CONT’D)
I like, really want to get
hydrangeas, but man the maintenance
on those things. Or is it
poinsettias.
INSERT EMAIL ON COMPUTER SCREEN: "Drew - Your preparedness in
the IC meeting did not meet expectations. Lets talk - Joan."
Drew slams the laptop shut. He pulls the quilt forward, head
between his legs and takes several breaths.
AP (O.S.) (CONT’D)
This is like, really pretty.
EXT. MARTY'S HAMPTON HOME - FRONT - THE NEXT DAY
A line of cars [PRODUCT USE - CARS] await the entrance of a luxury Hamptons home.
Music bumps in the distance.
LOW ON: Two pairs of Ferragamo shoes [PRODUCT USE - MEN’S LUXURY SHOES] exiting a luxury car.
We move upwards, transitioning into their POV: They merge
with other PARTYGOERS dressed to the theme: Pastels and
Pajamas. The music grows louder.
Suddenly, the door swings open, revealing Marty in silk
pajamas, holding a bottle of Veuve Clicquot [VISIBLE LOGO - CHAMPAGNE].
MARTY
Let the bacchanal commence!
They rush in.
INT/EXT. MARTY'S HAMPTON HOME - DAY
We PUSH THROUGH a classic Hampton's party as Marty guides
Drew and AP—Hannah on his arm. Dozens of 30-something
PARTYGOERS dressed to the nines in designer evening wear
huddle in social circles dispersed across the vast lawn.
Laughs, pictures, dancing. Young 20-something influencers
live stream the festivities. Reaching the back lawn:
22.
MARTY
Party favors are in the sunroom.
Photo booth's on the north side of
the lawn; there's open bars on both
ends of the pool. Party hard, kids.
AP and Drew catch up to Marty and Hannah as they finish their
walkabout, a procession that would put any royal to shame.
HANNAH
(to AP and Drew)
And how are you boys?
DREW
Peachy.
AP
Everything is great!
HANNAH
My fave thing about men is that
you're all equally bad at lying.
(To Drew) Even the gay ones.
INT. MARTY'S HAMPTONS HOME - SUN ROOM - LATER
Drew cautiously walks into the room, peeking around. In the
center rests a long banquet dressed as a drug bar. From
behind we see LAUREN (mid 50s white woman, if Jennifer
Coolidge played Lucille Bluth) pop up from swiping her nose
across the table.
LAUREN
(spotting Drew)
If anyone asks, it's the new Laura
Mercier [PRODUCT MENTION - MAKEUP] setting powder.
She extends her hand. Drew joyfully accepts.
DREW
Drew.
LAUREN
Lauren.
(sizing him up)
Sweetpea, you look stressed.
Drew's a bit taken aback, thinking he was masking it well. He
looks across the bar, determined. His hand hovers over
various options. Then, a buzz on his work phone.
INSERT PHONE SCREEN JOAN: Have a sec?
Drew stares for a second, but pockets it. He quickly grabs a
laughably large shrooms chocolate bar. He snaps it in half.
23.
DREW
Bottoms up?
Lauren grins.
She taps her half to his in a cheers motion. Together, they
eat their halves...a beat.
DREW (CONT’D)
Wait, how much did we take?
LAUREN
Probably not enough.
She grabs Drew's hands and sways to the music.
INT. MARTY'S HAMPTON HOME - LIVING ROOM - MOMENTS LATER
Marty and Hannah navigate the growing crowd. Hannah grabs a
slice of lox from a nearby tray. It's good. She spots Anna
and Michael from a distance; she’s excited, while Marty
shrinks.
HANNAH
Did you sign the card?
Marty nods as they walk over.
INT. MARTY'S HAMPTON HOME - BEDROOM - LATER
Lauren and Drew sit crisscross atop Marty and Hannah's bed.
They gaze up at the ceiling, reaching above. In their druginduced
stupor, the ceiling pops like popcorn.
DREW
How do you know Marty?
LAUREN
Warner Music. 2018. I was his boss.
Now I'm everyone's boss.
Lauren mimes punching a ceiling above her. She makes a sound
effect of glass shattering. Then, suddenly quite serious -
LAUREN (CONT’D)
Never let them see you sweat. How
about you?
DREW
College. We’ve all been friends
since freshman year.
24.
LAUREN
Oh isn’t that sweet.
DREW
And are you happy?
LAUREN
What kind of question is that?
Drew neither laughs nor responds.
LAUREN (CONT’D)
You kids and your ‘happiness.’ Its
like - GenZ doesn’t drink. They
don’t have sex. Millennials want to
be “Happy.” My god. We glad our
asses off and are just happy that
ChatGPT hasn’t taken our jobs and
Social Security hasn’t run out.
Grow a pair already.
Lauren bursts out laughing, hard. Drew is bemused and feeling
the effects of the drugs. Lauren stands up and pulls Drew’s
hand forward into hers as she stands over him while he sits
on the edge of the bed.
LAUREN (CONT’D)
Alright, sweatpea. Tell me. What is
wrong with you.
DREW
Well, my mom died 6 months ago.
LAUREN
Ohhhh, I’m so sorry.
DREW
And for the first time in a decade
I actually stopped to think.
LAUREN
That’s good. Thinking is good.
DREW
I just started thinking. One day,
I’ll be dead.
LAUREN
You sure will be honey. Dead as a
doornail. We all will.
25.
DREW
And like, this life I’m leading. I
mean...
Drew hesitates.
LAUREN
Let it out. Its just us two. And
the universe. And your dead mom.
A beat.
DREW
I hate my job. The people are
awful. The work is boring. I don’t
care about...plastics. My husband --
I love him, but I don’t really like
him.
Lauren starts to flap Drew’s arms up and down as if they were
rope in an Equinox class.
DREW (CONT’D)
I want, I want...
A beat. Lauren encourages him to keep going. Drew
hyperventilates and screams:
DREW (CONT’D)
I want to make a change!
Drew, wide-eyed, is at the peak of his trip.
LAUREN
There you go, sweatpea! Now take
action!
An impulsive thought. Drew pulls out his work phone and types
out a response to JOAN: "No need to discuss. I quit."
DREW
I QUIT!
Drew stands up, blood rushing to his head. He grabs the
rubber ball from his pocket placing it on the bed, holding it
in place. He looks around the room for a blunt object. He
finds a pair of nail scissors on Hannah’s changing table and
stumbles over to the bed. Lauren stands up to get out of the
way, encouraging him further:
LAUREN
Do it, sweatpea! Own your destiny!
26.
DREW
Ahhhhh!
Drew plunges the nail scissors into the red ball. The impact
makes a dent in the ball, but breaks the scissors. A beat.
Drew stares at the dented ball. It hasn’t popped. His
shoulders slump.
LAUREN
Oh. I wonder what that means.
INT. MARTY'S HAMPTON HOME - KITCHEN - LATER
Marty walks into his kitchen where half dressed CATERERS and
BARTENDERS (20s mix of beautiful men and women) in bowties
and beachwear [PRODUCT USE - MEN’S SWIMWEAR] prepare the next batch of drinks, drugs and
snacks. A server prepares a tray of small gatorade bottles
and pipettes as Marty’s phone alarm goes off.
MARTY
Hey - mind if I snag one of those.
Marty - a bit slower and in a daze, takes one of the Gatorade
bottles. He slushes the drinks around in his mouth, almost
gurgling. After a beat, Hannah walks in.
HANNAH
There you are.
She goes for a quick snack off the trays and smiles at her
staff.
HANNAH (CONT’D)
Anna and I were thinking of a
sunset cruise after Parrish, how
does that sound?
MARTY
Sure.
Hannah senses something is off, beyond the obvious drugs.
Marty sways back and forth, exaggerating his high to hide
behind it.
HANNAH
Martin?
MARTY
I didn’t make Partner.
27.
Hannah looks around to see if anyone is near them. A lone
caterer looks up, bemused as he spreads cheese on crackers.
Hannah grabs Marty and pulls him into the nearby mudroom.
INT. MARTY’S HAMPTON HOME - MUDROOM - MOMENTS LATER
Hannah closes the door, turns on the dryer and the faucet in
the sink.
MARTY
What are you doing?
HANNAH
(in a hushed voice) So no one can
hear us!
Hannah begins to pace back and forth and takes deep breaths.
HANNAH (CONT’D)
Ok, I was afraid this would happen,
but its ok. I will put aside the
fact that you LIED to me.
MARTY
I’m really sorry I lied I just...
A beat. Hannah composes herself and has a sudden realization.
HANNAH
Wait - what was your rating?
MARTY
What?
HANNAH
Martin - what was your goddamn
rating!? And tell me the truth.
MARTY
Very Strong.
Hannah perks up, relieved. She grabs Marty’s arm and they sit
down on the bench.
HANNAH
That’s good! I mean its not
Distinctive, but you know...its
good. (a beat). Very Good. It just
means that you have to hustle the
next six months and you’ll get it
next time.
28.
MARTY
But...
HANNAH
We...you have worked too hard not
to hit this milestone. And I’ve
already told everyone you are, so
you will be. Besides I already
signed off on a renovation with the
bonus you were supposed to have.
MARTY
Why did you sign off on a $250,000
renovation without mentioning it to
me?
HANNAH
Why did you lie to my face and risk
making me look like a fool?
Silence. The two sit for a beat, frustrated with the other
and themselves. Hannah’s phone buzzes, then does Marty’s:
others are looking for them.
MARTY
Can we just go back out there and
pretend this never happened?
Hannah stands up and composes herself. Marty reaches for her
hand with a sweet smile, which she receives - reluctantly.
She fixes her hair, takes a deep breath and goes back
outside. Marty sits a few beats longer and turns off the
dryer and the faucet. He looks out through the door at the
party scene with a heavy bit of melancholy.
AP (O.S.)
Hey Marty, get out here! I think
Drew poked a hole in your bed!
EXT. TWO MILE HOLLOW BEACH - EVENING
Drew, AP, and Marty huddle in front of a bonfire at the beach
looking out at the ocean, laying on beach chairs with an
earshot from the waves that crash onshore. Drew takes deep
breaths. His high dissipating, but still present. Marty
pulls out three waters from a cooler and hands one to Drew
and AP along with a small pack of pills.
MARTY
Here, take this.
29.
AP takes the water and pills without hesitation, Drew looks
down at what Marty gave him with trepidation.
MARTY (CONT’D)
5-HTP, an Advil and Magnesium.
Chill.
Drew takes the pills and downs the water. A few beats pass.
They each look exhausted and a bit lost. Finally, AP stands
up, breaking the tension.
AP
Alright, who wants to go first?
Drew and Marty look on with confusion.
AP (CONT’D)
Come on. On the count!
As he says it, both Drew and Marty know exactly what he means
and sink back into their lawn chairs. They don’t want to hear
it.
MARTY
Read the room, dude.
AP
On the count! One, two,
Drew nods at Marty, who gives in. The two join in their
college ritual, but with a fraction of AP’s enthusiasm.
DREW
Three!
MARTY
Three!
In rapid fire succession...
AP
My dad kicked me out of my
apartment, cut me off financially
and I’m looking for roommates in
Bushwick.
MARTY
I lied to Hannah about making
partner, she spent money we don’t
have and now we both have to
pretend until I hopefully make it
next cycle. She might divorce me if
I don’t.
30.
DREW
While I was high I quit my job and
realized that I don’t like Ben very
much. He wants to do another round
of IVF and I’m not sure that I do.
Marty and AP look to Drew and then each other in anguish.
DREW (CONT’D)
Do I win?
MARTY
I mean. Its actually kind of a tie
between the two of you.
AP
I am homeless you know.
MARTY
Good, now you know what struggle
looks like. Maybe you’ll actually
paint something worth the paper its
on.
AP throws sand at Marty. They both laugh. Drew sits silent
for a moment.
AP
Well Drew, think of it this way -
You can be a lady who lunches and
do pilates with Hannah. Maybe
she’ll tell you where she’s hid
Marty’s balls.
Marty throws sand back at AP.
AP (CONT’D)
That had a rock in it!
All three burst out laughing.
CUT TO BLACK.
THE END CREDITS BEGIN.
MUSIC CUE: "I only want to be with you" by Dusty Springfield.
31.`;

export const ScriptUpload: React.FC<ScriptUploadProps> = ({
  onAnalysisTriggered,
  onBack,
  onUploadSuccess,
}) => {
  const { project } = useProjectStore();
  const projectId = project?._id;
  const projectTitle = project?.title ?? 'Untitled Project';
  const [scriptText, setScriptText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<UploadedFileStatus[]>([]);
  const [uploadLimits, setUploadLimits] = useState<{
    max_file_size_bytes: number;
    max_file_size_mb: number;
    supported_file_types: string[];
    supported_extensions: string[];
  } | null>(null);
  
  // FIX 1: Add state for the Script Title, defaulting to a sensible value
  const [scriptTitle, setScriptTitle] = useState(`${projectTitle} - Manual Entry`);

  const charCount = scriptText.length;
  // Use filter(Boolean) to ensure split on whitespace doesn't result in empty strings being counted
  const wordCount = scriptText.trim().split(/\s+/).filter(Boolean).length;

  // Load upload limits on component mount
  useEffect(() => {
    const loadUploadLimits = async () => {
      try {
        const limits = await apiClient.getUploadLimits();
        setUploadLimits(limits);
      } catch (error) {
        console.error('Failed to load upload limits:', error);
        // Set default limits if API call fails
        setUploadLimits({
          max_file_size_bytes: 104857600, // 100MB
          max_file_size_mb: 100,
          supported_file_types: ['application/pdf'],
          supported_extensions: ['.pdf']
        });
      }
    };
    loadUploadLimits();
  }, []);

  const validateFile = (file: File): string | null => {
    if (!uploadLimits) return null;

    // Check file size
    if (file.size > uploadLimits.max_file_size_bytes) {
      return `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size of ${uploadLimits.max_file_size_mb}MB. Please use a smaller file.`;
    }

    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!uploadLimits.supported_extensions.includes(fileExtension)) {
      return `File type not supported. Please use one of: ${uploadLimits.supported_extensions.join(', ')}`;
    }

    return null;
  };

  const loadSampleScript = () => {
    setScriptText(SAMPLE_SCRIPT);
    // FIX 2: Also update the title when loading the sample script
    setScriptTitle(`${projectTitle} - Sample Script`);
    toast({
      title: 'Sample Script Loaded',
      description: 'You can now analyze this sample or replace it with your own.',
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0 && projectId) {
      // Validate files before processing
      const validFiles: File[] = [];
      const invalidFiles: { file: File; error: string }[] = [];

      Array.from(files).forEach(file => {
        const validationError = validateFile(file);
        if (validationError) {
          invalidFiles.push({ file, error: validationError });
        } else {
          validFiles.push(file);
        }
      });

      // Show errors for invalid files
      invalidFiles.forEach(({ file, error }) => {
        toast({
          title: 'File Validation Error',
          description: `${file.name}: ${error}`,
          variant: 'destructive',
        });
      });

      // Only process valid files
      if (validFiles.length === 0) {
        return;
      }

      const newFiles = validFiles.map(file => ({
        file,
        status: 'pending' as const
      }));
      
      setSelectedFiles(prev => [...prev, ...newFiles]);
      setIsLoading(true);

      try {
        // Upload files sequentially
        for (const fileStatus of newFiles) {
            // Update status to uploading
            setSelectedFiles(prev => prev.map(f =>
                f.file === fileStatus.file ? { ...f, status: 'uploading' } : f
            ));

            try {
                const result = await apiClient.uploadScript({
                    projectId: projectId,
                    file: fileStatus.file,
                    title: fileStatus.file.name,
                });

                // Update status to success
                setSelectedFiles(prev => prev.map(f =>
                    f.file === fileStatus.file ? { ...f, status: 'success', scriptId: result.script_id } : f
                ));
                
                // If it's the last file, trigger success callback for navigation if needed
                // For multiple files, we might want to stay on page or navigate to dashboard
                // For now, let's toast
                toast({
                    title: 'Uploaded',
                    description: `${fileStatus.file.name} uploaded successfully.`,
                });

                // If this is the only file or last file, maybe we trigger onUploadSuccess?
                // But onUploadSuccess expects a single scriptId.
                // We'll defer navigation until user chooses or just use the last one.
                if (newFiles.length === 1) {
                     onUploadSuccess(result.script_id);
                }

            } catch (error) {
                console.error(`Error uploading ${fileStatus.file.name}:`, error);
                let errorMessage = 'Unknown error';
                
                // Extract the actual error message from the API response
                if (error instanceof Error) {
                    errorMessage = error.message;
                } else if (error && typeof error === 'object' && 'detail' in error) {
                    errorMessage = (error as any).detail;
                } else if (error && typeof error === 'object' && 'message' in error) {
                    errorMessage = (error as any).message;
                }
                
                setSelectedFiles(prev => prev.map(f =>
                    f.file === fileStatus.file ? { ...f, status: 'error', error: errorMessage } : f
                ));

                // Show specific error message, especially for file size violations
                toast({
                    title: 'Upload Failed',
                    description: errorMessage,
                    variant: 'destructive',
                });
            }
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAnalyzeText = async () => {
    // FIX 5: Critical check to ensure projectId is available (in case state hasn't propagated)
    if (!projectId || projectId.length !== 24) {
      toast({
        title: 'Error',
        description: 'Project ID is missing or invalid. Please re-create the project.',
        variant: 'destructive',
      });
      console.error("Critical Error: projectId is missing or invalid before API call.", projectId);
      return;
    }

    // Client-side validation to prevent sending empty data (required by backend)
    const trimmedText = scriptText.trim();
    if (!trimmedText) {
      toast({
        title: 'Error',
        description: 'Script content cannot be empty.',
        variant: 'destructive',
      });
      return;
    }
    
    // Check minimum length before analyzing
    if (trimmedText.length < 200) {
      toast({
        title: 'Error',
        description: 'Script must be at least 200 characters.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      // FIX 3: Pass the title explicitly in the API call
      const result = await apiClient.uploadScript({
        projectId: projectId,
        content: trimmedText,
        title: scriptTitle.trim() || `${projectTitle} Script`, // Ensure title is not empty
      });
      
      toast({
        title: 'Uploaded',
        description: 'Script uploaded successfully. Analysis in progress.',
      });
      onUploadSuccess(result.script_id);
    } catch (error) {
      console.error('Upload error:', error);
      // Log the specific error message to help identify if the field missing is still project_id
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      toast({
        title: 'Upload Unsuccessful',
        description: 'Please use text-based pdf files and try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isAnalyzeDisabled = isLoading || charCount < 200 || !scriptTitle.trim();

  return (
    <div className="min-h-screen bg-cream-yellow pt-16">
      <div className="mx-auto max-w-4xl">
        <Button variant="ghost" onClick={onBack} className="mb-4" disabled={isLoading}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Project
        </Button>

        <h1 className="mb-2 text-3xl font-bold text-slate-900">Upload Script File</h1>
        <p className="mb-8 text-slate-600">
          Paste your script for "{projectTitle}"
        </p>

        <Card className="bg-white rounded-lg shadow-md">
          <CardHeader>
            <CardTitle>Script Content</CardTitle>
            <CardDescription>
              Upload what you have right now. Artie looks for patterns and potential, not polish. Your work stays private.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              {/* FIX 4: Added title input field */}
              <Input
                type="text"
                className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={scriptTitle}
                onChange={(e) => setScriptTitle(e.target.value)}
                placeholder={`e.g., ${projectTitle} - Scene 1`}
                disabled={isLoading}
                aria-label="Script Title"
              />
              
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept={uploadLimits?.supported_extensions.join(',') || ".pdf"}
                    multiple
                    onChange={handleFileChange}
                    className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={isLoading}
                    aria-label="Script Files"
                    value=""
                  />
                  <Button
                    variant="outline"
                    onClick={loadSampleScript}
                    disabled={isLoading}
                    className="w-full sm:w-auto"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Load Sample Script
                  </Button>
                </div>
                
                {uploadLimits && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <AlertCircle className="h-3 w-3" />
                    <span>
                      Maximum file size: {uploadLimits.max_file_size_mb}MB.
                      Supported formats: {uploadLimits.supported_extensions.join(', ')}
                    </span>
                  </div>
                )}
              </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-2 mt-4">
                    <h4 className="text-sm font-medium text-gray-700">Uploaded Files</h4>
                    {selectedFiles.map((fileStatus, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                            <span className="text-sm text-gray-600 truncate max-w-[200px]">{fileStatus.file.name}</span>
                            <div className="flex items-center gap-2">
                                {fileStatus.status === 'uploading' && <span className="text-xs text-blue-500">Uploading...</span>}
                                {fileStatus.status === 'success' && <span className="text-xs text-green-500">Uploaded</span>}
                                {fileStatus.status === 'error' && <span className="text-xs text-red-500">Error</span>}
                                {fileStatus.status === 'pending' && <span className="text-xs text-gray-400">Pending</span>}
                            </div>
                            <button
                                onClick={() => {
                                    // Remove file from list
                                    setSelectedFiles(prev => prev.filter(f => f.file !== fileStatus.file));
                                    
                                    // If file was already uploaded (has scriptId), we might want to delete it from backend too
                                    // But for now, UI removal is sufficient for the 'selected files' stage before project creation/finalization context.
                                    // If this is the standalone uploader, files are uploaded immediately.
                                    // So we should probably call delete endpoint if scriptId exists.
                                    if (fileStatus.scriptId) {
                                        apiClient.deleteScript(fileStatus.scriptId).catch(console.error);
                                    }
                                }}
                                className="text-slate-400 hover:text-red-500 ml-2"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
              )}

              <Textarea
                className="min-h-[400px] font-mono text-sm"
                placeholder="Paste your script here..."
                value={scriptText}
                onChange={(e) => setScriptText(e.target.value)}
                disabled={isLoading}
              />

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div>
                  {wordCount.toLocaleString()} words · {charCount.toLocaleString()} characters
                </div>
                <Button
                  onClick={handleAnalyzeText}
                  disabled={isAnalyzeDisabled}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isLoading ? 'Uploading...' : 'Upload & Analyze'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
